/**
 * Footer Collision Detection
 * Moves fixed buttons (rewards, chat) above footer when visible
 * Uses transform instead of bottom to avoid layout shifts (CLS)
 */

/**
 * Initialize footer avoidance behavior
 */
export function initFooterAvoidance(): void {
  const footerEl = document.querySelector<HTMLElement>('footer, .footer, [role="contentinfo"]');
  if (!footerEl) return;

  const footer = footerEl;
  let rewardsButton: HTMLElement | null = null;
  let chatButton: HTMLElement | null = null;
  let chatPlaceholder: HTMLElement | null = null;
  let chatObserver: MutationObserver | null = null;
  let currentTranslateY = 0;

  // Watch for Chatwoot trying to change the bottom position and override it
  function setupChatObserver(): void {
    if (!chatButton || chatObserver) return;

    chatObserver = new MutationObserver(() => {
      if (currentTranslateY && chatButton) {
        chatButton.style.setProperty(
          "transform",
          `translateY(-${currentTranslateY}px)`,
          "important"
        );
      }
    });

    chatObserver.observe(chatButton, { attributes: true, attributeFilter: ["style"] });
  }

  function applyTransform(el: HTMLElement, offsetY: number): void {
    if (offsetY > 0) {
      el.style.setProperty("transform", `translateY(-${offsetY}px)`, "important");
    } else {
      el.style.removeProperty("transform");
    }
  }

  function updateButtonPositions(): void {
    // Cache button references (they may load async)
    if (!rewardsButton) {
      rewardsButton = document.querySelector<HTMLElement>(".frcp-loyalty-launcher-wrapper");
      if (rewardsButton) {
        rewardsButton.style.setProperty("will-change", "transform");
        rewardsButton.style.setProperty(
          "transition",
          "transform 0.3s ease-out, background 0.3s",
          "important"
        );
      }
    }
    if (!chatButton) {
      chatButton = document.querySelector<HTMLElement>(".woot-widget-bubble");
      if (chatButton) {
        chatButton.style.setProperty("will-change", "transform");
        chatButton.style.setProperty(
          "transition",
          "transform 0.3s ease-out, background 0.3s",
          "important"
        );
        setupChatObserver();
      }
    }
    if (!chatPlaceholder) {
      chatPlaceholder = document.getElementById("chatwoot-placeholder");
      if (chatPlaceholder) {
        chatPlaceholder.style.setProperty("will-change", "transform");
        chatPlaceholder.style.setProperty("transition", "transform 0.3s ease-out", "important");
      }
    }

    if (!rewardsButton && !chatButton && !chatPlaceholder) return;

    const footerRect = footer.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const footerTop = footerRect.top;

    if (footerTop < viewportHeight) {
      const footerDistanceFromBottom = viewportHeight - footerTop;
      const buffer = 10;
      // Calculate how much to shift up from default position
      const defaultBottom = 24;
      const neededBottom = Math.max(40, footerDistanceFromBottom + buffer);
      currentTranslateY = neededBottom - defaultBottom;

      if (rewardsButton) applyTransform(rewardsButton, currentTranslateY);
      if (chatButton) applyTransform(chatButton, currentTranslateY);
      if (chatPlaceholder && chatPlaceholder.style.display !== "none") {
        applyTransform(chatPlaceholder, currentTranslateY);
      }
    } else {
      // Footer not visible - reset
      currentTranslateY = 0;
      if (rewardsButton) applyTransform(rewardsButton, 0);
      if (chatButton) applyTransform(chatButton, 0);
      if (chatPlaceholder && chatPlaceholder.style.display !== "none") {
        applyTransform(chatPlaceholder, 0);
      }
    }
  }

  // Reuse StickyHeader's scroll event when available so we do not add a second
  // global scroll listener on normal storefront pages.
  let ticking = false;
  const scheduleUpdate = (): void => {
    if (ticking) return;

    window.requestAnimationFrame(() => {
      updateButtonPositions();
      ticking = false;
    });
    ticking = true;
  };

  if (document.querySelector("sticky-header.header-wrapper")) {
    document.addEventListener("header:scroll", scheduleUpdate);
  } else {
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
  }
  window.addEventListener("resize", scheduleUpdate, { passive: true });

  // Initial check with retry for async buttons
  let retryCount = 0;
  const checkButtons = (): void => {
    updateButtonPositions();
    retryCount++;
    if (!rewardsButton && !chatButton && retryCount < 10) {
      setTimeout(checkButtons, 500);
    }
  };
  checkButtons();
}
